/**
 * AuthCallback Page
 *
 * Handles OAuth and email confirmation link callbacks from Supabase
 */

import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, CheckCircle } from 'lucide-react';
import { supabase, createUserProfile } from '../../lib/supabase';

export function AuthCallback() {
  const navigate = useNavigate();
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('processing'); // processing, success, error

  useEffect(() => {
    const handleCallback = async () => {
      try {
        // Get the hash parameters from the URL
        const hashParams = new URLSearchParams(window.location.hash.substring(1));
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');

        // If we have tokens in hash, set the session
        if (accessToken && refreshToken) {
          const { error: setSessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken
          });
          if (setSessionError) throw setSessionError;
        }

        // Get current session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();

        if (sessionError) {
          throw sessionError;
        }

        if (session?.user) {
          // Check if user profile exists
          const { data: existingProfile } = await supabase
            .from('users')
            .select('user_type')
            .eq('id', session.user.id)
            .single();

          // If no profile exists, create one from user metadata
          if (!existingProfile) {
            const userType = session.user.user_metadata?.user_type || 'seller';
            const email = session.user.email;
            const domain = email.split('@')[1].toLowerCase();

            // Find company by email domain
            let companyInfo = { company: null, foundInBuyers: false, foundInSellers: false };

            if (userType === 'buyer') {
              const { data: buyerMatch } = await supabase
                .from('buyers')
                .select('id, company_name')
                .contains('verified_email_domains', [domain])
                .limit(1)
                .single();

              if (buyerMatch) {
                companyInfo = { company: buyerMatch, foundInBuyers: true, foundInSellers: false };
              }
            } else {
              const { data: sellerMatch } = await supabase
                .from('sellers')
                .select('id, company_name')
                .contains('verified_email_domains', [domain])
                .limit(1)
                .single();

              if (sellerMatch) {
                companyInfo = { company: sellerMatch, foundInBuyers: false, foundInSellers: true };
              }
            }

            // Create user profile
            await createUserProfile(session.user, userType, companyInfo);

            setStatus('success');

            // Redirect after short delay to show success
            setTimeout(() => {
              if (userType === 'buyer') {
                navigate('/buyer/search', { replace: true });
              } else {
                navigate('/seller/dashboard', { replace: true });
              }
            }, 2000);
          } else {
            // Profile exists, redirect immediately
            if (existingProfile.user_type === 'buyer') {
              navigate('/buyer/search', { replace: true });
            } else {
              navigate('/seller/dashboard', { replace: true });
            }
          }
        } else {
          // No session, redirect to login
          navigate('/login', { replace: true });
        }
      } catch (err) {
        console.error('Auth callback error:', err);
        setError(err.message);
        setStatus('error');
        // Redirect to login after a delay
        setTimeout(() => navigate('/login', { replace: true }), 3000);
      }
    };

    handleCallback();
  }, [navigate]);

  if (status === 'error') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <p className="text-red-600 mb-4">Error: {error}</p>
          <p className="text-gray-500">Redirecting to login...</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center p-8">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Email Verified!</h1>
          <p className="text-gray-600">Welcome to Pharmunity. Redirecting to dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="text-center">
        <Loader2 className="w-12 h-12 animate-spin text-blue-600 mx-auto mb-4" />
        <p className="text-gray-600">Verifying your email...</p>
      </div>
    </div>
  );
}

export default AuthCallback;
